import React, { useRef, useEffect, useState } from 'react';
import { Mode, ModelId, KnowledgeBaseId } from '../../types';
import { AvailableModel, KnowledgeBase, Subscription, TokenUsage } from '../../services/ai/fastApiBackend';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select';
import { Settings, LogOut } from 'lucide-react';

interface ChatInputProps {
  input: string;
  setInput: (value: string) => void;
  onSubmit: () => void;
  isStreaming: boolean;
  mode: Mode;
  onClearChat: () => void;
  selectedModel: ModelId;
  setSelectedModel: (model: ModelId) => void;
  selectedKnowledgeBase: KnowledgeBaseId;
  setSelectedKnowledgeBase: (knowledgeBase: KnowledgeBaseId) => void;
  availableModels: AvailableModel[];
  knowledgeBases: KnowledgeBase[];
  tokenUsage?: TokenUsage | null;
  subscription?: Subscription | null;
  isDisabled?: boolean;
  standalone?: boolean;
  onSignOut?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  onSubmit,
  isStreaming,
  mode,
  onClearChat,
  selectedModel,
  setSelectedModel,
  selectedKnowledgeBase,
  setSelectedKnowledgeBase,
  availableModels,
  knowledgeBases,
  tokenUsage,
  subscription,
  isDisabled = false,
  standalone = false,
  onSignOut
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Set model to "none" if no available models
  useEffect(() => {
    if (availableModels.length === 0 && selectedModel !== 'none') {
      setSelectedModel('none' as ModelId);
    }
  }, [availableModels, selectedModel, setSelectedModel]);

  // Close the settings popover on an outside click.
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settingsOpen]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isStreaming && !isDisabled) {
        onSubmit();
      }
    }
  };

  const formatMultiplier = (m: number): string =>
    `${Number.isInteger(m) ? m : m.toFixed(1)}× usage`;

  // Model options with a "none" fallback when no models are available. The hint surfaces
  // relative usage cost (baseline = 1×, pricier models 1.5×/2×/…).
  const modelOptions: SearchableSelectOption[] = availableModels.length > 0
    ? availableModels.map(m => ({
        value: m.id,
        label: m.name,
        hint: formatMultiplier(m.usage_multiplier ?? 1),
      }))
    : [{ value: 'none', label: 'None' }];

  const knowledgeBaseOptions: SearchableSelectOption[] = [
    { value: 'none', label: 'None' },
    ...knowledgeBases.map(kb => ({ value: kb.id, label: kb.name })),
  ];

  const pickersDisabled = isStreaming || isDisabled;
  const menuItem =
    'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50';

  // End-user token meter (TASK-037 Phase 0). Per-org; hidden if the backend didn't send usage.
  const tokenPctUsed =
    tokenUsage && tokenUsage.total_tokens > 0
      ? Math.min(100, Math.round((tokenUsage.used_tokens / tokenUsage.total_tokens) * 100))
      : 0;
  const tokenResetLabel = tokenUsage?.resets_at
    ? new Date(tokenUsage.resets_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  // Subscription UX (TASK-037 Phase 3). Surfaces the plan + billing cycle from /auth/user-info.
  // status "none" (legacy orgs) → show nothing; the raw token meter above is the fallback.
  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
  const hasSub = !!subscription && subscription.status !== 'none';
  const subStatus = subscription?.status;
  const isTerminal = subStatus === 'expired' || subStatus === 'canceled';
  const isTrialing = subStatus === 'trialing';
  const endingSoon = isTrialing && subscription?.days_left != null && subscription.days_left <= 3;
  // Action needed → show the amber upgrade/contact CTA.
  const subNeedsAction = isTerminal || subStatus === 'past_due' || subStatus === 'paused' || endingSoon;

  const subPill =
    subStatus === 'trialing' ? { text: 'Trial', cls: 'bg-indigo-100 text-indigo-700' }
    : subStatus === 'active'
      ? subscription?.cancel_at_period_end
        ? { text: 'Ending', cls: 'bg-amber-100 text-amber-700' }
        : { text: 'Active', cls: 'bg-emerald-100 text-emerald-700' }
    : subStatus === 'past_due' ? { text: 'Past due', cls: 'bg-amber-100 text-amber-700' }
    : subStatus === 'paused' ? { text: 'Paused', cls: 'bg-muted text-muted-foreground' }
    : subStatus === 'expired' ? { text: 'Expired', cls: 'bg-red-100 text-red-700' }
    : subStatus === 'canceled' ? { text: 'Canceled', cls: 'bg-red-100 text-red-700' }
    : { text: '', cls: '' };

  const daysLeftLabel =
    subscription?.days_left != null
      ? `${subscription.days_left} ${subscription.days_left === 1 ? 'day' : 'days'} left`
      : null;
  const subDetail =
    isTrialing ? (daysLeftLabel ? `Trial — ${daysLeftLabel}` : 'Trial')
    : subStatus === 'active'
      ? subscription?.cancel_at_period_end
        ? (fmtDate(subscription?.current_period_end ?? null) ? `Ends ${fmtDate(subscription!.current_period_end)}` : 'Ending soon')
        : (fmtDate(subscription?.current_period_end ?? null) ? `Renews ${fmtDate(subscription!.current_period_end)}` : null)
    : subStatus === 'past_due' ? 'Payment overdue'
    : subStatus === 'paused' ? 'Plan paused'
    : isTerminal ? 'Plan ended'
    : null;

  // Low-balance nudge: ≤10% remaining but not yet empty (the hard 402 wall is the final stop).
  const tokenPctRemaining =
    tokenUsage && tokenUsage.total_tokens > 0
      ? (tokenUsage.remaining_tokens / tokenUsage.total_tokens) * 100
      : 100;
  const lowBalance =
    !!tokenUsage && tokenUsage.total_tokens > 0 && tokenUsage.remaining_tokens > 0 && tokenPctRemaining <= 10;

  return (
    <div className="flex items-end gap-2 border-t p-2">
      <div ref={settingsRef} className="relative self-center">
        <Button
          variant="ghost"
          size="icon"
          title="Settings"
          className="h-8 w-8 flex-shrink-0"
          onClick={() => setSettingsOpen((o) => !o)}
        >
          <Settings className="h-4 w-4 m-auto" />
        </Button>

        {settingsOpen ? (
          <div className="absolute bottom-full left-0 z-50 mb-1 w-64 max-w-[calc(100vw-1rem)] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md">
            <div className="space-y-3">
              {/* Subscription: plan + billing cycle (Phase 3). Hidden for legacy orgs (status "none"). */}
              {hasSub ? (
                <div className="rounded-md border border-border px-2 py-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium truncate">
                      {subscription?.plan_name || 'Plan'}
                    </span>
                    {subPill.text ? (
                      <span className={`ml-2 flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${subPill.cls}`}>
                        {subPill.text}
                      </span>
                    ) : null}
                  </div>
                  {subDetail ? (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{subDetail}</div>
                  ) : null}
                  {subNeedsAction ? (
                    <div className="mt-1 text-[11px] font-medium text-amber-700">
                      {isTrialing ? 'Trial ending soon — contact your admin to upgrade.'
                        : isTerminal ? 'Your plan has ended — contact your admin to renew.'
                        : 'Contact your admin to update your plan.'}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Token usage meter (per-org) */}
              {tokenUsage ? (
                <div className="rounded-md border border-border px-2 py-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">Tokens</span>
                    <span className="text-muted-foreground">
                      {tokenUsage.remaining_tokens.toLocaleString()} left
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${tokenPctUsed >= 90 ? 'bg-red-500' : 'bg-primary'}`}
                      style={{ width: `${tokenPctUsed}%` }}
                    />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>of {tokenUsage.total_tokens.toLocaleString()}</span>
                    {tokenResetLabel ? <span>resets {tokenResetLabel}</span> : null}
                  </div>
                  {/* Low-balance nudge (≤10% left) — softer than the hard 402 wall. */}
                  {lowBalance ? (
                    <div className="mt-1.5 rounded-sm bg-amber-50 px-1.5 py-1 text-[10px] font-medium text-amber-700">
                      Running low — contact your admin to add tokens.
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Model picker (searchable) */}
              <div>
                <div className="px-1 pb-1 text-xs font-medium text-muted-foreground">Model</div>
                <SearchableSelect
                  title="Model"
                  side="top"
                  value={selectedModel}
                  onChange={(value) => setSelectedModel(value as ModelId)}
                  options={modelOptions}
                  disabled={pickersDisabled}
                  searchPlaceholder="Search models…"
                  emptyText="No models"
                />
              </div>

              {/* Knowledge-base picker (searchable) */}
              <div>
                <div className="px-1 pb-1 text-xs font-medium text-muted-foreground">
                  Knowledge base
                </div>
                <SearchableSelect
                  title="Knowledge base"
                  side="top"
                  value={selectedKnowledgeBase}
                  onChange={(value) => setSelectedKnowledgeBase(value as KnowledgeBaseId)}
                  options={knowledgeBaseOptions}
                  disabled={pickersDisabled}
                  searchPlaceholder="Search knowledge bases…"
                  emptyText="No knowledge bases"
                />
              </div>

              <div className="h-px bg-border" />

              <button
                type="button"
                onClick={() => {
                  onClearChat();
                  setSettingsOpen(false);
                }}
                disabled={isStreaming || isDisabled}
                className={`${menuItem} text-foreground`}
              >
                Clear History
              </button>

              {/* Sign out: standalone (browser) only — in an Office host the identity is brokered
                  by the host. Intentionally NOT gated by isDisabled so unlicensed users can
                  switch account. */}
              {standalone && onSignOut ? (
                <button
                  type="button"
                  onClick={() => {
                    onSignOut();
                    setSettingsOpen(false);
                  }}
                  disabled={isStreaming}
                  className={`${menuItem} text-red-600`}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <Textarea
        ref={textareaRef}
        placeholder={mode === "compose" ? "What do you want to write?" : "Ask me anything..."}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
          }
        }}
        onKeyDown={handleKeyDown}
        className="min-h-[3rem] max-h-[12rem] resize-none"
        rows={1}
        disabled={isStreaming || isDisabled}
      />
    </div>
  );
};
